<template>
  <div v-if="loading" class="left-0 top-0 h-full w-full py-20">
    <TheLoader />
  </div>
  <div v-else>
    <div v-if="isAuthorized">
      <ChartTab
        v-if="data.length"
        :subtitle="subtitle"
        :product="product"
        :check-class-colors="checkClassColors"
        :circle-class-colors="circleClassColors"
        :capacity-unit="unit"
        :colors="colors"
        :hide-y-label="false"
        :is-lithium-ion-batteries="false"
        :truncate-labels="true"
        :raw-data="data"
        :regions="regions"
      />
    </div>

    <OverviewTab
      v-else
      :subtitle="subtitle"
      :descriptions="descriptions"
      :packages="packages"
      :subscribe="subscribe"
    >
      <template #description>
        <div
          class="flex flex-row items-center justify-between gap-x-2 pt-4 xl:pt-16"
        >
          <CommonImage
            :src="images.big.src"
            classes="max-h-[250px] min-h-[250px] w-full rounded-lg bg-p-500 object-cover lg:w-[60%] xl:max-h-[240px] xl:min-h-[240px] xl:w-[68%]"
            alt=""
            aria-hidden="true"
            fetch-priority="auto"
          />
          <CommonImage
            :src="images.small.src"
            classes="hidden max-h-[250px] min-h-[250px] w-full rounded-lg bg-p-500 object-cover sm:block sm:w-[40%] xl:max-h-[240px] xl:min-h-[240px] xl:w-[30%]"
            alt=""
            aria-hidden="true"
            fetch-priority="auto"
          />
        </div>
      </template>
    </OverviewTab>
  </div>
</template>

<script setup lang="ts">
import ChartTab from '~/components/capacity-database/ChartTab.vue'
import OverviewTab from '~/components/capacity-database/OverviewTab.vue'
import {
  CAPACITY_DATABASE_SUBSCRIPTIONS,
  CD_UNAUTHORIZED_DESCRIPTIONS,
  METHODOLOGY_FILE_INFO,
} from '~/src/constants/capacity-database/common'
import { useAuthentication } from '~/composables/useAuthentication'
import { useCapacityDatabaseData } from '~/composables/useCapacityDatabaseData'
import {
  CD_ANODE_CHECK_COLOR_CLASS,
  CD_ANODE_CIRCLE_COLOR_CLASS,
  CD_ANODE_COLORS,
  CD_ANODE_NAME,
  CD_ANODE_SEO,
  CD_ANODE_SLUG,
  CD_ANODE_SUBTITLE,
  CD_ANODE_UNAUTHORIZED_IMAGES,
  CD_ANODE_UNAUTHORIZED_PACKAGES,
  CD_ANODE_UNAUTHORIZED_SUBSCRIBE,
  CD_ANODE_UNIT,
} from '~/src/constants/capacity-database/anode'

import CommonImage from '~/src/components/common/images/CommonImage.vue'

/** app */
const { getSeoInfo } = useSeo()
const seo = getSeoInfo(CD_ANODE_SEO)

definePageMeta({ isTab: true })

/** refs */
const product = ref(CD_ANODE_NAME)
const unit = ref(CD_ANODE_UNIT)
const colors = ref(CD_ANODE_COLORS)
const circleClassColors = ref(CD_ANODE_CIRCLE_COLOR_CLASS)
const checkClassColors = ref(CD_ANODE_CHECK_COLOR_CLASS)
const subscription = ref(CAPACITY_DATABASE_SUBSCRIPTIONS[CD_ANODE_SLUG])
const subtitle = ref(CD_ANODE_SUBTITLE)
const packages = ref(CD_ANODE_UNAUTHORIZED_PACKAGES)
const descriptions = ref(CD_UNAUTHORIZED_DESCRIPTIONS)
const images = ref(CD_ANODE_UNAUTHORIZED_IMAGES)
const subscribe = ref(CD_ANODE_UNAUTHORIZED_SUBSCRIBE)
const file = ref(null)

const { loading, isAuthorized, data, regions, fetchChartDataIfAuthorized } =
  useCapacityDatabaseData({
    capacitySlug: CD_ANODE_SLUG,
    subscription: subscription.value,
  })

/** app */
const { $config } = useNuxtApp()

/** lifecycle */
onMounted(() => {
  if (!isAuthorized.value) {
    const methodologyInfo = METHODOLOGY_FILE_INFO[CD_ANODE_SLUG]
    file.value = {
      ...methodologyInfo,
      ID: $config.public[methodologyInfo.EnvKey],
    }
    subscribe.value = {
      ...CD_ANODE_UNAUTHORIZED_SUBSCRIBE,
      methodology: file.value,
    }
  }
  fetchChartDataIfAuthorized()
})
useAuthentication(fetchChartDataIfAuthorized)
useHead(seo)
</script>
